import { Box, Paper, Skeleton, Typography } from '@mui/material';
import type { ReportJobTypes } from '@vy/types';
import { REPORT_JOB_TYPES } from '@vy/types';
import { isNotNullOrUndefined, raiseUnreachable, withDefault } from '@vy/utils';
import { Suspense } from 'react';
import { VyTableToolbar } from '../../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { useSearchWithSearchParams } from '../../../../hooks/use-search-with-search-params';
import { REPORTS_WITH_DISABLED_ACTIONS } from '../reports-page-consts/report-page-content-consts';
import { useReportTypeStateParam } from '../reports-page-hooks/report-type-state-param-hook';
import type { ReportType } from '../reports-page-types/report-type';
import { REPORT_TYPES } from '../reports-page-types/report-type';
import { ArAgingReportSection } from './ar-aging-report-section/ArAgingReportSection';
import { useUnapprovedInvoicesDownloadProps } from './report-download-hooks/use-unapproved-invoices-download-props';
import { ReportJobsSection } from './report-jobs-section/ReportJobsSection';
import { ReportMenu } from './ReportMenu';
import { useUsageReportDownloadProps } from './report-download-hooks/use-usage-report-download-props';
import { ReportSearchField } from './ReportSearchField';

export const ReportsPageContent: React.FC = () => {
  const [searchText, setSearchText] = useSearchWithSearchParams();
  const [reportType] = useReportTypeStateParam();
  const selectedReportJob = getReportJobType(reportType ?? REPORT_TYPES.COMMITMENT_USAGE);
  const hasReportJob = isNotNullOrUndefined(selectedReportJob);
  const isArAging = reportType === REPORT_TYPES.AR_AGING;

  const unapprovedInvoicesDownloadProps = useUnapprovedInvoicesDownloadProps();
  const usageReportDownloadProps = useUsageReportDownloadProps();

  const downloadReportProps = {
    ...unapprovedInvoicesDownloadProps,
    ...usageReportDownloadProps,
  };

  return (
    <Suspense
      fallback={
        <Skeleton variant="rectangular" sx={{ borderRadius: '10px', height: '100%', position: 'relative' }} />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: "20px",
            mb: 2
          }}>
          {reportType}
        </Typography>

        <Paper
          variant="outlined"
          sx={(theme) => ({
            borderRadius: '12px',
            top: '20px',
            boxShadow: theme.shadows[1],
            height: `calc(100vh - ${theme.spacing(12.5)})`,
            display: 'flex',
            flexDirection: 'column',
          })}
        >
          {hasReportJob && (
            <>
              <VyTableToolbar sx={{ pt: 4, pb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 2
                  }}>
                  <ReportSearchField
                    defaultValue={withDefault(searchText, '')}
                    onChange={(event) => setSearchText(event.target.value)}
                  />
                </Box>

                <VyTableToolbar.Actions>
                  {isNotNullOrUndefined(reportType) &&
                    !REPORTS_WITH_DISABLED_ACTIONS.includes(reportType) && (
                      <ReportMenu
                        selectedReportJob={selectedReportJob}
                        reportType={reportType}
                        shouldExportAsCsv={false}
                        shouldRequestReport
                      />
                    )}
                </VyTableToolbar.Actions>
              </VyTableToolbar>

              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <ReportJobsSection
                  {...downloadReportProps}
                  reportJobType={selectedReportJob}
                  filter={searchText ?? undefined}
                />
              </Box>
            </>
          )}

          {isArAging && <ArAgingReportSection />}
        </Paper>
      </Box>
    </Suspense>
  );
};

export function getReportJobType(reportType: ReportType): ReportJobTypes | undefined {
  switch (reportType) {
    case REPORT_TYPES.COMMITMENT_USAGE:
      return REPORT_JOB_TYPES.CommitmentUsage;
    case REPORT_TYPES.UNCHARGED_USAGE:
      return REPORT_JOB_TYPES.UnchargedUsage;
    case REPORT_TYPES.PRODUCTS_USAGE:
      return REPORT_JOB_TYPES.ProductsUsage;
    case REPORT_TYPES.CUSTOMER_REVENUE:
      return REPORT_JOB_TYPES.CustomerRevenue;
    case REPORT_TYPES.CUSTOMERS:
      return REPORT_JOB_TYPES.Customers;
    case REPORT_TYPES.CONTRACTS:
      return REPORT_JOB_TYPES.Contracts;
    case REPORT_TYPES.RECURRING_REVENUE:
      return REPORT_JOB_TYPES.RecurringRevenue;
    case REPORT_TYPES.ANONYMOUS_CUSTOMERS:
      return REPORT_JOB_TYPES.AnonymousCustomers;
    case REPORT_TYPES.MONTHLY_CLOSURE:
      return REPORT_JOB_TYPES.MonthlyClosure;
    case REPORT_TYPES.UPCOMING_INVOICES:
      return REPORT_JOB_TYPES.UpcomingInvoices;
    case REPORT_TYPES.FAILED_INVOICE_DELIVERIES:
      return REPORT_JOB_TYPES.FailedInvoiceDeliveries;
    case REPORT_TYPES.USAGE:
      return REPORT_JOB_TYPES.Usage;
    case REPORT_TYPES.UNAPPROVED_INVOICES:
    case REPORT_TYPES.AR_AGING:
    case REPORT_TYPES.ALL_REPORTS:
    case REPORT_TYPES.USAGE_REPORT_V2:
    case REPORT_TYPES.COMMITMENT_VS_USAGE:
    case REPORT_TYPES.CUSTOMER_MASTER:
    case REPORT_TYPES.NRR_GRR:
    case REPORT_TYPES.JOURNAL_ENTRIES:
    case REPORT_TYPES.UNMETERED_EVENTS:
      return undefined;
    default:
      return raiseUnreachable(reportType);
  }
}
